import { useState, type FormEvent } from 'react';
import { useSession } from '../lib/store';
import { ApiError } from '../lib/api';
import { Button } from '../components/ui';
import { DISCORD_INVITE } from '../lib/links';

/**
 * The front door.
 *
 * One action. A visitor who has never been here presses Play Now and is a
 * trainer with a Grove and a warming egg before they have read the second
 * paragraph — no username to invent, no password to remember, no email to
 * check. Signing in is a link, not a form, because the only people who need it
 * already know they have an account.
 *
 * There is deliberately no registration form on this screen at all. Creating a
 * permanent account is a thing you do *after* you have a collection worth
 * protecting, which is what `/claim` is for.
 */
export function LoginPage() {
  const { login, playAsGuest } = useSession();
  const [showSignIn, setShowSignIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'guest' | 'login' | null>(null);

  async function startPlaying() {
    setError(null);
    setBusy('guest');
    try {
      await playAsGuest();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');
      setBusy(null);
    }
  }

  async function onSignIn(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy('login');
    try {
      await login(username, password);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 'var(--gap)' }}>
      <div style={{ width: 'min(440px, 100%)' }} className="stack">
        {/* Wordmark. The logo carries the name, so no duplicate <h1> text —
            the alt text keeps it as the page's accessible heading. */}
        <div className="center stack-sm">
          <h1 style={{ margin: 0 }}>
            <img
              src="/logo.png"
              alt="PokéGrove"
              style={{
                width: 'min(340px, 88%)',
                height: 'auto',
                margin: '0 auto',
                filter: 'drop-shadow(0 6px 10px rgba(0, 0, 0, 0.5))',
                animation: 'bob 4.2s ease-in-out infinite',
              }}
            />
          </h1>
          <p style={{ color: 'rgba(253,243,223,.82)', fontWeight: 700, fontSize: 'var(--text-sm)' }}>
            Hatch eggs. Grow a collection. Help other trainers along.
          </p>
        </div>

        <div className="pxframe pxframe--raised panel">
          <div className="stack">
            <Button
              type="button"
              variant="primary"
              size="lg"
              block
              loading={busy === 'guest'}
              onClick={() => void startPlaying()}
            >
              🎮 Play Now
            </Button>

            <p className="tiny muted center" style={{ margin: 0 }}>
              No account needed. You'll get a trainer name, your first Grove and an egg that
              hatches in about a minute.
            </p>

            {error && <div className="error-text">{error}</div>}

            <div className="center" style={{ paddingTop: 'var(--gap-sm)' }}>
              {!showSignIn ? (
                <button
                  type="button"
                  className="linkbtn tiny"
                  onClick={() => {
                    setShowSignIn(true);
                    setError(null);
                  }}
                >
                  Already have an account? Sign in
                </button>
              ) : (
                <button
                  type="button"
                  className="linkbtn tiny"
                  onClick={() => {
                    setShowSignIn(false);
                    setError(null);
                  }}
                >
                  Never mind — take me to the game
                </button>
              )}
            </div>

            {showSignIn && (
              <form onSubmit={onSignIn} className="stack">
                <div className="field">
                  <label className="field__label" htmlFor="username">
                    Username
                  </label>
                  <input
                    id="username"
                    className="input"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    autoComplete="username"
                    required
                    maxLength={18}
                    placeholder="ashketchum"
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="password">
                    Password
                  </label>
                  <input
                    id="password"
                    className="input"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <Button type="submit" variant="go" block loading={busy === 'login'}>
                  Sign in
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Below the panel rather than inside it: the front door has exactly one
            action, and a second button beside Play Now would be a fork in the
            road on the screen that exists not to have one. */}
        <p className="tiny center" style={{ color: 'rgba(253,243,223,.72)', margin: 0 }}>
          <a href={DISCORD_INVITE} target="_blank" rel="noreferrer noopener" style={{ color: 'inherit' }}>
            Join the PokéGrove Discord ↗
          </a>
        </p>

        <p className="tiny center" style={{ color: 'rgba(253,243,223,.62)' }}>
          PokéGrove is an unofficial fan project. Pokémon and Pokémon character names are trademarks of
          Nintendo. Data and sprites courtesy of PokéAPI.
        </p>
      </div>
    </div>
  );
}
